import React, {useCallback, useEffect, useState} from "react";
import {Alert, Collapse, Link, Stack, Typography} from "@mui/material";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {listCobrancasDaFatura} from "../FinanceiroService";
import {useAcoesDaCobranca} from "../useAcoesDaCobranca";
import CobrancaCard from "./CobrancaCard";
import EventosDaCobranca from "./EventosDaCobranca";
import RegistrarPagamentoDialog from "./RegistrarPagamentoDialog";

/*
 * As cobranças da fatura: uma por meio (boleto com Pix e cartão), com o boleto para pagar, o histórico de cada uma e
 * as ações. `aoMudar` avisa a fatura depois de um pagamento, porque a situação dela vem das cobranças.
 */
export default function CobrancasDaFatura({faturaId, aoMudar}) {
    const [cobrancas, setCobrancas] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [aberta, setAberta] = useState(null);

    const carregar = useCallback(async () => {
        if (!faturaId) return;
        setCarregando(true);
        setErro('');
        try {
            const response = await listCobrancasDaFatura(faturaId);
            setCobrancas(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            setErro(getBackendErrorMessage(error, 'Não foi possível carregar as cobranças da fatura.'));
        } finally {
            setCarregando(false);
        }
    }, [faturaId]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const depoisDoPagamento = useCallback(async () => {
        await carregar();
        await aoMudar?.();
    }, [carregar, aoMudar]);

    const acoes = useAcoesDaCobranca(depoisDoPagamento);

    if (carregando && !cobrancas.length) {
        return <Typography color="text.secondary">Carregando as cobranças...</Typography>;
    }
    if (erro) {
        return <Alert severity="error">{erro}</Alert>;
    }
    if (!cobrancas.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                Esta fatura ainda não tem cobranças. Elas são criadas no fechamento, uma por meio de pagamento.
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            {acoes.aviso.success ? <Alert severity="success" onClose={acoes.limparAviso}>{acoes.aviso.success}</Alert> : null}
            {acoes.aviso.error ? <Alert severity="error" onClose={acoes.limparAviso}>{acoes.aviso.error}</Alert> : null}
            {cobrancas.map((cobranca) => (
                <Stack key={cobranca.id} spacing={0.5}>
                    <CobrancaCard
                        cobranca={cobranca}
                        ocupado={acoes.ocupado}
                        onRegistrarPagamento={acoes.abrirPagamento}
                        onReenviarBoleto={acoes.reenviar}
                    />
                    {cobranca.eventos?.length ? (
                        <>
                            <Link component="button" type="button" variant="body2" sx={{alignSelf: 'flex-start'}}
                                  onClick={() => setAberta(aberta === cobranca.id ? null : cobranca.id)}>
                                {aberta === cobranca.id ? 'Esconder o histórico' : `Ver o histórico (${cobranca.eventos.length})`}
                            </Link>
                            <Collapse in={aberta === cobranca.id} unmountOnExit>
                                <EventosDaCobranca eventos={cobranca.eventos} />
                            </Collapse>
                        </>
                    ) : null}
                </Stack>
            ))}
            <RegistrarPagamentoDialog
                open={Boolean(acoes.pagando)}
                cobranca={acoes.pagando}
                onClose={acoes.fecharPagamento}
                onConfirm={acoes.registrar}
            />
        </Stack>
    );
}
